import { useMemo, useState } from "react";
import { Check, ChevronDown, Search } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface MultiSelectOption {
	value: string;
	label: string;
	/** Chú thích phụ bên phải nhãn (vd. số hồ sơ) — không tham gia tìm kiếm. */
	hint?: string;
}

interface MultiSelectProps {
	options: readonly MultiSelectOption[];
	values: string[];
	onChange: (values: string[]) => void;
	/** Nhãn hiển thị khi chưa chọn gì. Rỗng luôn mang nghĩa "tất cả", không phải "không có gì". */
	placeholder?: string;
	/** Lời nhắc trong ô tìm kiếm. */
	searchPlaceholder?: string;
	emptyText?: string;
	/** Class cho NÚT mở, không phải wrapper — đặt bề rộng ở container bao ngoài. */
	className?: string;
	disabled?: boolean;
	id?: string;
}

/**
 * Chọn nhiều giá trị trong một popover có ô tìm kiếm.
 *
 * KHÔNG dùng `<select multiple>`: control gốc buộc người dùng giữ Ctrl để chọn
 * rời rạc và bỏ chọn sạch khi lỡ click, thứ mà một bộ lọc danh sách dài không
 * chịu được. Nút mở cao `h-9` để nằm cùng hàng với `Input`/`Select`/`Button`
 * theo quy ước một-chiều-cao-cho-cả-hàng của dự án.
 */
export function MultiSelect({
	options,
	values,
	onChange,
	placeholder = "Tất cả",
	searchPlaceholder = "Tìm...",
	emptyText = "Không có lựa chọn nào",
	className,
	disabled = false,
	id,
}: MultiSelectProps) {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");

	const selected = useMemo(() => new Set(values), [values]);

	const filtered = useMemo(() => {
		const needle = query.trim().toLowerCase();
		if (!needle) return options;
		return options.filter((option) => option.label.toLowerCase().includes(needle));
	}, [options, query]);

	const toggle = (value: string) => {
		onChange(selected.has(value) ? values.filter((v) => v !== value) : [...values, value]);
	};

	// Nút này thao tác trên DANH SÁCH ĐANG LỌC, không phải toàn bộ options: sau
	// khi gõ tìm kiếm, "chọn tất cả" mà lại chọn cả những mục không nhìn thấy là
	// một thay đổi câm.
	const allFilteredSelected =
		filtered.length > 0 && filtered.every((option) => selected.has(option.value));

	const toggleFiltered = () => {
		if (allFilteredSelected) {
			const removing = new Set(filtered.map((o) => o.value));
			onChange(values.filter((v) => !removing.has(v)));
		} else {
			onChange([...new Set([...values, ...filtered.map((o) => o.value)])]);
		}
	};

	const triggerLabel = values.length === 0 ? placeholder : `Đã chọn ${values.length}`;

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger
				id={id}
				disabled={disabled}
				className={cn(
					"flex h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background",
					"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
					"disabled:cursor-not-allowed disabled:opacity-50",
					className,
				)}
			>
				<span className={cn("truncate", values.length === 0 && "text-muted-foreground")}>
					{triggerLabel}
				</span>
				<ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
			</PopoverTrigger>

			<PopoverContent className="w-[min(22rem,calc(100vw-2rem))] p-0">
				<div className="flex items-center gap-2 border-b px-2">
					<Search className="h-4 w-4 shrink-0 text-muted-foreground" />
					<Input
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						placeholder={searchPlaceholder}
						className="h-9 border-0 px-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
					/>
				</div>

				<div className="flex items-center justify-between border-b px-2 py-1.5">
					<button
						type="button"
						onClick={toggleFiltered}
						disabled={filtered.length === 0}
						className="text-xs font-medium text-primary hover:underline disabled:opacity-50"
					>
						{allFilteredSelected ? "Bỏ chọn tất cả" : "Chọn tất cả"}
					</button>
					<button
						type="button"
						onClick={() => onChange([])}
						disabled={values.length === 0}
						className="text-xs text-muted-foreground hover:underline disabled:opacity-50"
					>
						Xoá lựa chọn
					</button>
				</div>

				{/* Cuộn nội bộ: danh sách người có thể dài, nhưng popover không được
				    cao quá màn hình rồi đẩy ô tìm kiếm ra ngoài tầm nhìn. */}
				<div className="max-h-64 overflow-y-auto p-1">
					{filtered.length === 0 ? (
						<p className="px-2 py-6 text-center text-sm text-muted-foreground">{emptyText}</p>
					) : (
						filtered.map((option) => {
							const isSelected = selected.has(option.value);
							return (
								<button
									key={option.value}
									type="button"
									role="option"
									aria-selected={isSelected}
									onClick={() => toggle(option.value)}
									className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground"
								>
									<span
										className={cn(
											"flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border border-primary",
											isSelected && "bg-primary text-primary-foreground",
										)}
									>
										{isSelected && <Check className="h-3 w-3" />}
									</span>
									<span className="min-w-0 flex-1 truncate">{option.label}</span>
									{option.hint && (
										<span className="shrink-0 text-xs text-muted-foreground">{option.hint}</span>
									)}
								</button>
							);
						})
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
}
